/**
 * NodePort utility — computes the four connection ports (top, bottom, left, right)
 * for a node and selects the correct pair based on the child's position zone
 * relative to the parent.
 *
 * The four zones (y increases downward):
 *
 *   Zone A — child is completely below parent (child.top >= parent.bottom):
 *             parent.bottom → child.top  (normal OST line)
 *
 *   Zone B — child's top has entered the parent's vertical range from below
 *             (parent.top <= child.top < parent.bottom):
 *             → NO LINE (return null)
 *
 *   Zone C — child's top is above parent's top but child's bottom still overlaps
 *             (child.top < parent.top AND child.bottom > parent.top):
 *             → connect via left/right ports depending on horizontal position.
 *               Child to the right → parent.right → child.left
 *               Child to the left  → parent.left  → child.right
 *
 *   Zone D — child is completely above parent (child.bottom <= parent.top):
 *             parent.top → child.bottom
 *
 * selectDominantAxisPorts (below) is a different, non-hierarchical port picker for the Plan
 * page's card connectors — see its own doc comment.
 */
import { NODE_W } from './canvasUtils';

export type PortSide = 'top' | 'bottom' | 'left' | 'right';

export interface Port {
    side: PortSide;
    x: number;
    y: number;
}

/** A positioned node shape used for port calculations. */
export interface PortableNode {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Minimum fraction of the smaller card's own cross-axis size that the two cards' ranges must
 * actually overlap by before selectDominantAxisPorts treats them as "aligned" on that axis (see
 * `crossAxisOverlapRatio` there). Below this, even though the ranges technically overlap by a few
 * units, that sliver isn't enough to read as aligned — the connector switches to the cross axis
 * for a 2-segment "L" bend instead of jogging a 3-segment "S" across what's really a near-miss.
 * Kept low so 2-segment routing is the common case, same-axis 3-segment routing the exception
 * (true alignment only) — see selectDominantAxisPorts's own doc comment.
 */
const CROSS_AXIS_ALIGNMENT_THRESHOLD = 0.15;

/**
 * Returns the four cardinal ports for a node, offset slightly away from the
 * node border so the line path starts/ends outside the node body.
 */
export function getNodePorts(node: PortableNode, gap = 6): Port[] {
    const { x, y, width, height } = node;
    return [
        { side: 'top', x, y: y - height / 2 - gap },
        { side: 'bottom', x, y: y + height / 2 + gap },
        { side: 'left', x: x - width / 2 - gap, y },
        { side: 'right', x: x + width / 2 + gap, y },
    ];
}

/**
 * Selects the correct port pair between a parent (`from`) and child (`to`) node
 * based purely on four positional zones.  Returns `null` for Zone B (no line).
 * `gap` is forwarded to getNodePorts for both nodes — see its own doc comment; only the
 * resulting port coordinates are affected, not which zone/side is picked (that's still decided
 * purely from the rects' own x/y/width/height).
 */
export function selectBestPorts(
    from: PortableNode,
    to: PortableNode,
    gap = 6,
): { from: Port; to: Port } | null {
    const fromPorts = getNodePorts(from, gap);
    const toPorts = getNodePorts(to, gap);

    const parentTop = from.y - from.height / 2;
    const parentBottom = from.y + from.height / 2;
    const parentLeft = from.x - from.width / 2;
    const parentRight = from.x + from.width / 2;
    const childTop = to.y - to.height / 2;
    const childBottom = to.y + to.height / 2;
    const childLeft = to.x - to.width / 2;
    const childRight = to.x + to.width / 2;

    const horizontalOverlap = childLeft < parentRight && childRight > parentLeft;

    // Zone A: child is completely below parent → normal bottom→top line.
    if (childTop >= parentBottom) {
        return {
            from: fromPorts.find((p) => p.side === 'bottom')!,
            to: toPorts.find((p) => p.side === 'top')!,
        };
    }

    // Zone D: child is completely above parent → parent top → child bottom.
    if (childBottom <= parentTop) {
        return {
            from: fromPorts.find((p) => p.side === 'top')!,
            to: toPorts.find((p) => p.side === 'bottom')!,
        };
    }

    // Vertical overlap exists from here on (childTop < parentBottom AND childBottom > parentTop).

    // Zone B: child's top has entered the parent's vertical band from below AND nodes overlap
    //         horizontally (child is still mostly "inside" the parent area) → no line.
    if (horizontalOverlap && childTop >= parentTop) {
        return null;
    }

    // Zone C: vertical overlap exists but either:
    //   a) the child is horizontally separated (beside the parent), or
    //   b) the child's top is above the parent's top (partially sliding out from the top).
    //   → connect via left/right ports based on which side the child centre is on.
    if (to.x >= from.x) {
        // Child is to the right (or same x) → parent right → child left.
        return {
            from: fromPorts.find((p) => p.side === 'right')!,
            to: toPorts.find((p) => p.side === 'left')!,
        };
    } else {
        // Child is to the left → parent left → child right.
        return {
            from: fromPorts.find((p) => p.side === 'left')!,
            to: toPorts.find((p) => p.side === 'right')!,
        };
    }
}

/**
 * A different, non-hierarchical port picker for the Plan page's card connectors (see
 * buildRectConnectorPath's `axisBias` param) — unlike selectBestPorts's OST-tailored zone system
 * (which treats "child below/above parent, aligned horizontally" as the default and only routes
 * left/right once a vertical overlap forces the issue — the right call for a parent-child tree
 * edge, but not for two otherwise-unrelated cards placed anywhere relative to each other), this
 * picks whichever axis the two rects are actually FARTHER apart on — the axis a connector
 * between them would naturally read as running along — and always returns a port pair (there's
 * no Plan equivalent of Zone B's "child sliding into its parent" suppression).
 *
 * Concretely: for each axis, the signed distance between the two rects' extents on that axis
 * (positive = a real gap between them, negative = how deeply they overlap instead — see
 * `verticalSeparation`/`horizontalSeparation` below) is compared; whichever is larger (a bigger
 * gap, or a shallower/no overlap) is the dominant axis. Two cards stacked with a large vertical
 * gap but aligned (overlapping) horizontally get a large positive vertical separation against a
 * negative horizontal one, so vertical wins regardless of how big that gap is — matching Zone A's
 * own default. Two cards placed diagonally, with neither axis overlapping at all, route via
 * whichever gap is smaller (the axis they're closer to being aligned on) — the case
 * selectBestPorts gets wrong for Plan's freely-placed cards, always defaulting to vertical
 * there purely because SOME vertical gap exists, however much smaller the horizontal one is.
 *
 * The FROM port always sits on that dominant axis (bottom/top, or left/right). The TO port,
 * though, only matches the same axis when the cards are actually aligned on the OTHER axis — a
 * real range overlap there of at least CROSS_AXIS_ALIGNMENT_THRESHOLD of the smaller card's own
 * cross-axis size (see `horizontalOverlap`/`verticalOverlap` below): that's the one case where a
 * same-axis pair draws a clean, near-straight line. A sliver of overlap below that threshold
 * doesn't count as aligned — everywhere else the TO port switches to the CROSS axis instead
 * (left/right when FROM exited top/bottom, and vice versa), on whichever side of the target card
 * actually faces the parent (`to.x >= from.x` etc. below) — turning what would otherwise be a
 * 3-segment "S" elbow (a parallel middle segment jogging across the gap between two same-axis
 * ports) into a single-bend, 2-segment "L" (see buildRectConnectorPath, which reads FROM/TO's side
 * orientation to pick between the two path shapes). This is the "prioritize 2 segments, changing
 * the target point to the side or top of the target card depending on its position relative to
 * the parent" behavior the Plan page's connectors want — biased, via a deliberately low alignment
 * threshold, toward the 2-segment "L" as the common case.
 */
export function selectDominantAxisPorts(
    from: PortableNode,
    to: PortableNode,
    gap = 6,
    heightBias = 0,
    widthBias = 0,
): { from: Port; to: Port } {
    const fromPorts = getNodePorts(from, gap);
    const toPorts = getNodePorts(to, gap);

    const parentTop = from.y - from.height / 2;
    const parentBottom = from.y + from.height / 2;
    const parentLeft = from.x - from.width / 2;
    const parentRight = from.x + from.width / 2;
    const childTop = to.y - to.height / 2;
    const childBottom = to.y + to.height / 2;
    const childLeft = to.x - to.width / 2;
    const childRight = to.x + to.width / 2;

    const verticalSeparation = Math.max(childTop - parentBottom, parentTop - childBottom);
    const horizontalSeparation = Math.max(childLeft - parentRight, parentLeft - childRight);

    // Biases (canvas units, converted from a fraction of the smaller node's own size on that
    // axis) pad one axis's separation before comparing — a way to favor staying on one axis
    // rather than flipping the instant the other's gap is even slightly bigger. Equal
    // height/width biases cancel out exactly (only their DIFFERENCE shifts the comparison), so 0/0
    // (the default) is a pure "whichever gap is actually bigger wins" comparison.
    const verticalBias = heightBias * Math.min(from.height, to.height);
    const horizontalBias = widthBias * Math.min(from.width, to.width);

    // Whether the two cards' ranges overlap on each axis by at least CROSS_AXIS_ALIGNMENT_THRESHOLD
    // of the smaller card's own size on that axis — "aligned" enough that entering/exiting on the
    // OTHER (perpendicular) axis alone reads as a straight line, so there's no need to bend onto
    // the cross axis just to reach the target. A smaller overlap than that doesn't count: it reads
    // as a near-miss, not an alignment, so the TO port switches to the cross axis instead (see
    // below) rather than jogging a 3-segment "S" across it.
    const horizontalOverlapAmount = Math.min(childRight, parentRight) - Math.max(childLeft, parentLeft);
    const verticalOverlapAmount = Math.min(childBottom, parentBottom) - Math.max(childTop, parentTop);
    const horizontalOverlap = horizontalOverlapAmount / Math.min(from.width, to.width) >= CROSS_AXIS_ALIGNMENT_THRESHOLD;
    const verticalOverlap = verticalOverlapAmount / Math.min(from.height, to.height) >= CROSS_AXIS_ALIGNMENT_THRESHOLD;

    if (verticalSeparation + verticalBias >= horizontalSeparation + horizontalBias) {
        const fromSide = to.y >= from.y ? 'bottom' : 'top';
        const fromPort = fromPorts.find((p) => p.side === fromSide)!;
        if (horizontalOverlap) {
            const toSide = to.y >= from.y ? 'top' : 'bottom';
            return { from: fromPort, to: toPorts.find((p) => p.side === toSide)! };
        }
        const toSide = to.x >= from.x ? 'left' : 'right';
        return { from: fromPort, to: toPorts.find((p) => p.side === toSide)! };
    }

    const fromSide = to.x >= from.x ? 'right' : 'left';
    const fromPort = fromPorts.find((p) => p.side === fromSide)!;
    if (verticalOverlap) {
        const toSide = to.x >= from.x ? 'left' : 'right';
        return { from: fromPort, to: toPorts.find((p) => p.side === toSide)! };
    }
    const toSide = to.y >= from.y ? 'top' : 'bottom';
    return { from: fromPort, to: toPorts.find((p) => p.side === toSide)! };
}

/**
 * Converts an edge node record to a PortableNode using the provided height lookup.
 */
export function toPortableNode(
    node: { id: string; x?: number; y?: number },
    nodeHeights: Record<string, number>,
    fallbackHeight: number,
): PortableNode {
    return {
        id: node.id,
        x: node.x ?? 0,
        y: node.y ?? 0,
        width: NODE_W,
        height: nodeHeights[node.id] ?? fallbackHeight,
    };
}
